import IconProfile from "../media/IconProfile.png"
import { useState } from "react"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { State } from "../state"
import { getProfileUrl } from "../utils"
import { DexxButton } from "../components/light/LightButton"
import { PageDescription, TitleColorWarning } from '../components/typography/Typogtaphy'

function ListItem(props: {label: string, description: string | JSX.Element, active: boolean,
    setActive: () => void, link?: string, disabled?: boolean, hide?: boolean}): JSX.Element {
    const [hovered, setHovered] = useState(false)

    if (props.hide) return <></>

    const isActive = hovered || props.active
    const isTextDescription = typeof props.description === "string"

    return (
        <li className="relative grid md:grid-cols-2 items-center font-light font-roboto gap-2 md:gap-8 md:text-right">
            {props.disabled ? (
                <div className="text-gray">{props.label}</div>
            ) : (
                <Link
                    to={props.link ?? "/menu"}
                    className={`transition-colors duration-300 ease-in-out py-1 
            ${isActive ? "text-succ2" : "text-white"}`}
                    onMouseEnter={() => {
                        setHovered(true)
                        props.setActive()
                    }}
                    onMouseLeave={() => setHovered(false)}
                >
                    {props.label}
                </Link>
            )}

            {/* Text description (inline, doesn’t affect row height much) */}
            {isTextDescription && isActive && (
                <div
                    className={`text-white md:text-xl text-lg text-left transition-all duration-300 ease-in-out
            ${isActive ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"}`}
                >
                    {props.description}
                </div>
            )}

            {/* JSX description (only visible when active) */}
            {!isTextDescription && isActive && (
                <>
                    {/* Mobile: show below label only when active */}
                    <div className="block md:hidden col-span-2 w-full mt-2 text-left">
                        <div className="flex justify-center">{props.description}</div>
                    </div>

                    {/* Desktop: absolutely positioned, floating on right half */}
                    <div className="hidden md:block absolute right-0 top-48 transform -translate-y-1/2 w-1/2 text-left">
                        <div className="flex justify-center">{props.description}</div>
                    </div>
                </>
            )}
        </li>
    )
}

export default function MenuRoute(): JSX.Element {
    interface LinkInfo {
        label: string,
        description: string | any,
        link?: string,
        hide?: boolean,
        disabled?: boolean
    }
    const [activeIndex, setActiveIndex] = useState<number>(-1)
    const basicAccountState = useSelector((state: State) => state.basicAccount)
    const detailAccountState = useSelector((state: State) => state.detailAccount)

    const isInvestor = basicAccountState.payload?.is_investor ?? false
    const isIssuer = basicAccountState.payload?.is_issuer ?? false
    const profileDoneMissingKYC = Boolean(detailAccountState.payload?.last_name?.trim())
    const kycSubmitted = basicAccountState.payload?.kyc_submitted ?? false

    const loading = basicAccountState.loading || detailAccountState.loading
    const kycDone = profileDoneMissingKYC && kycSubmitted

    const profileDescriptionIfNotKYC = <>
        <div className="grid md:text-xl text-lg">
            <div className="grid justify-items-center px-5">
                <div className="font-roboto py-2 tracking-wider right-text">
                    <img src={IconProfile} className={"h-16 ml-5"} alt="Complete The KYC" />
                    <PageDescription title={"Almost done!"}
                        description={"Regulations require that we collect this information to confirm your identity"}
                        titleColor={"warning"} titleSize="4xl" descrSize="base" />
                    <div>
                        <TitleColorWarning className="text-xl">Know Your Customer</TitleColorWarning>
                        <label className="w-full text-tertiary text-base font-light py-4">
                            To complete the KYC you will need your passport.<br />
                            This will take about 5 minutes and must be done in a single sitting.</label>
                    </div>
                    <div className="flex justify-center mt-6">
                        <Link className="" to={"/kyc"}>
                            <DexxButton  className="rounded-lg px-5 py-2 cursor-pointer z-0"
                                label="Begin the KYC process"
                                color1="warning" color2="warning" color3="warning_lighter"/>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </>

    const items: LinkInfo[] = [
        {
            label: "My Profile", link: getProfileUrl(basicAccountState, detailAccountState), disabled: loading,
            description: kycDone ? "Here you can see a recap of your account information." : profileDescriptionIfNotKYC
        },
        {
            label: "My issuer portfolio", link: "/issuer/portfolio", hide: !(kycDone && isIssuer),
            description: "Here you can manage and create assets and tokens."
        },
        {
            label: "My investor portfolio", link: "/portfolio", hide: !(kycDone && isInvestor),
            description: "Here you will fund your wallet and see a summary of your investment portfolio holdings."
        },
        {
            label: "Trade", link: "/invest", disabled: loading,
            description: "Here you will use your wallet funds to purchase one or more available tokens."
        },
        {
            label: "Earn", link: "/earn", disabled: loading, hide: !(isInvestor || isIssuer),
            description: "Here you will use your wallet funds to purchase one or more available tokens."
        },
        {
            label: "F.A.Q.", link: "/support", disabled: loading,
            description: "Look here for answers to the most frequently asked questions."
        },
        {
            label: "My investor portfolio", description: "", disabled: true, hide: isInvestor
        },
        {
            label: "Loyalty Program", description: "", disabled: true
        },
        {
            label: "Education", description: "", disabled: true
        }

    ]

    return <>
        {loading ?
            <></> :
            <div className="grid flex-1 place-items-center">
                <ul className="grid p-0 font-light text-3xl md:text-5xl leading-10 tracking-wide list-none gap-2 w-full">
                    {items.map((item, idx) => (
                        <ListItem key={idx} link={item.link} label={item.label} disabled={item.disabled}
                            hide={item.hide} description={item.description} active={idx === activeIndex}
                            setActive={() => setActiveIndex(idx)} />
                    ))}
                </ul>
            </div>
        }

    </>
}